import React from "react";
import "../../styles/Policies.css"

/**
 * HIPAA Authorization for disclosure of health information to a third party.
 *
 * DRAFT — NOT YET REVIEWED BY COUNSEL. Written against 45 CFR 164.508, which
 * lists six core elements an authorization must contain and three statements it
 * must carry to be valid. An authorization missing any of them is defective and
 * the disclosure made under it is not permitted.
 *
 * Scope note, because this is easy to get wrong: a patient asking for a copy of
 * their OWN records does not need an authorization. That is a right of access
 * under 164.524, and the Medical Records screen exercises it — the export is
 * emailed to the address on the patient's own account. This document is for the
 * other case: the patient asking Youro to send their information to someone
 * else, such as another physician, an employer, an insurer or a family member.
 *
 * It therefore has to be completed per request, naming that recipient and what
 * is being sent. It is NOT something to bundle into signup consent: 164.508(b)(3)
 * forbids compounding an authorization with other documents, and 164.508(b)(4)
 * forbids conditioning treatment on signing one. Presenting this alongside the
 * Terms at registration would invalidate it.
 *
 * Nothing in the app implements this flow yet. The document is written so the
 * feature can be built against it; see the note in the mobile Privacy & Security
 * screen.
 */

const HipaaAuthorization = () => {
    return (
        <>
        <div className="policies">
            <h2> Authorization for Release of Health Information</h2>
            <div className=".policies-content">
                <p>Use this authorization to ask Youro to send your health information to someone other than you. You do not need it to get a copy of your own records — you can request those from Medical Records in the app, and they will be sent to the email address on your account.</p>

                <p><strong>Who holds the information</strong></p>
                <p>Youro Health, Inc. and the medical groups and providers who have treated you through the Youro platform.</p>

                <p><strong>Who may receive it</strong></p>
                <p>The person or organization you name in your request, including their name and full contact details. If you want your information sent to more than one recipient, a separate authorization is needed for each.</p>

                <p><strong>What may be disclosed</strong></p>
                <p>You choose what is included. You may authorize release of your complete record held by Youro, or you may limit it — for example, to visit notes and care plans for a specific condition, to prescriptions, to symptom assessment results, or to records from a particular date range. Describe what you want released specifically enough that we can identify it.</p>
                <p>If your record contains information that receives special protection under state or federal law — for example, records relating to substance use disorder treatment, mental health, HIV status, or genetic testing — that information will only be released if you say so expressly in your request.</p>

                <p><strong>Purpose of the disclosure</strong></p>
                <p>State the purpose. If the release is at your own request and you do not wish to give a reason, you may state "at the request of the individual," which is sufficient.</p>

                <p><strong>Expiration</strong></p>
                <p>State a date or an event on which the authorization expires. If you do not, it expires one year from the date you sign it.</p>

                <p><strong>Signature</strong></p>
                <p>The authorization must be signed and dated by you, or by your personal representative. If it is signed by a representative, they must describe their authority to act for you, and we will verify it before releasing anything.</p>

                <p><strong>Your right to revoke</strong></p>
                <p>You may revoke this authorization at any time by writing to privacy@yourohealth.com or to the address in our Notice of Privacy Practices. A revocation takes effect when we receive it. It does not apply to anything we already disclosed while the authorization was in effect, and it does not apply where we have already acted in reliance on it.</p>

                <p><strong>Treatment is not conditioned on signing</strong></p>
                <p>We will not refuse to treat you, and will not condition your membership, your access to the Youro platform, or your eligibility for benefits, on whether you sign this authorization. Signing it is entirely your choice.</p>

                <p><strong>Information may lose its protection once disclosed</strong></p>
                <p>Once your information is disclosed to the recipient you name, it may be redisclosed by that recipient, and at that point it may no longer be protected by the HIPAA Privacy Rule. Whether it stays protected depends on who receives it: another healthcare provider is generally covered by the same rules, while an employer, an attorney, or a family member generally is not.</p>

                <p><strong>Your copy</strong></p>
                <p>You are entitled to a copy of any authorization you sign, and we will provide one.</p>

                <p><strong>How to submit a request</strong></p>
                <p>Email privacy@yourohealth.com. We will send you the form to complete and will confirm when the disclosure has been made.</p>
                <p>&nbsp;</p>
            </div>
       </div>
       </>
    )
}

export default HipaaAuthorization;
